"use client"

import { lazy, useEffect } from "react"
import { Routes, Route } from "react-router-dom"
import { useDispatch } from "react-redux"
import { useAuth } from "./hooks/useAuth"
import { fetchCategories } from "./redux/shop/categoriesSlice"
import { register } from "swiper/element/bundle"
import { SharedLayout } from "./components/SharedLayout/SharedLayout"
import { RestrictedRoute } from "./components/RestrictedRoute"
import { GlobalStyle } from "./styles/GlobalStyle"
import Admin from "./pages/Admin/Admin"
import PaymentSuccess from "./pages/Payment/PaymentSuccess"
import PaymentCancel from "./pages/Payment/PaymentCancel"
import ProfilePage from "./pages/Profile/ProfilePage"
import OrderHistory from "./pages/OrderHistory/OrderHistory"
import DashboardLayout from "./layouts/DashboardLayout"
import OrderDetailsPage from "./pages/OrderDetailsPage"
import VerifyOtp from "./components/AccountPage/VerifyOtp"
import PrivacyPolicy from "./pages/Privacy/PrivacyPolicy";
import TermsOfService from "./pages/Privacy/TermsOfService";

import { ToastContainer } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"

const HomePage = lazy(() => import("./pages/Home/Home"))
const AccountPage = lazy(() => import("./pages/Account/Account"))
const SignIn = lazy(() => import("./components/AccountPage/SignIn"))
const SignUp = lazy(() => import("./components/AccountPage/SignUp"))
const ForgetPassword = lazy(() => import("./components/AccountPage/ForgetPassword/ForgetPassword"))
const ShopPage = lazy(() => import("./pages/Shop/Shop"))
const WishlistPage = lazy(() => import("./pages/Wishlist/Wishlist"))
const CartPage = lazy(() => import("./pages/Cart/CartPage"))
const CheckoutPage = lazy(() => import("./pages/Checkout/Checkout"))
const DetailsPage = lazy(() => import("./pages/Details/Details"))
const Description = lazy(() => import("./components/DetailsPage/Description/Description"))
const AdditionalInfo = lazy(() => import("./components/DetailsPage/AdditionalInfo/AdditionalInfo"))
const Specs = lazy(() => import("./components/DetailsPage/Specs/Specs"))
const Reviews = lazy(() => import("./components/DetailsPage/Reviews/Reviews"))
const SupportPage = lazy(() => import("./pages/Support/Support"))
const NotFoundPage = lazy(() => import("./pages/NotFound/NotFound"))
const SettingsPage = lazy(() => import("./pages/Settings/SettingsPage"))
const ContactsPage = lazy(() => import("./pages/Contacts/ContactsPage"))

register()

const App = () => {
  const dispatch = useDispatch()
  const { checkIsUserLoggedIn } = useAuth()

  useEffect(() => {
    checkIsUserLoggedIn()
    dispatch(fetchCategories())
  }, [checkIsUserLoggedIn, dispatch])

  return (
      <>
        <Routes>
          <Route path="/" element={<SharedLayout />}>
            <Route index element={<HomePage />} />
            <Route path="/user-account" element={<RestrictedRoute component={<AccountPage />} />}>
              <Route path="sign-in" element={<SignIn />} />
              <Route path="sign-up" element={<SignUp />} />
              <Route path="forget-password" element={<ForgetPassword />} />
            </Route>
            <Route path="/user-account/verify" element={<VerifyOtp />} />
            <Route path="/shop/:category?" element={<ShopPage />} />
            <Route path="/shopping-cart" element={<CartPage />} />
            <Route path="/shopping-cart/checkout" element={<CheckoutPage />} />
            <Route path="/wishlist" element={<WishlistPage />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route path="/shop/:category/:slug" element={<DetailsPage />}>
              <Route index element={<Description />} />
              <Route path="additional-info" element={<AdditionalInfo />} />
              <Route path="specifications" element={<Specs />} />
              <Route path="reviews" element={<Reviews />} />
            </Route>
            <Route path="/profile" element={<DashboardLayout />}>
              <Route index element={<ProfilePage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="orders">
                <Route index element={<OrderHistory />} />
                <Route path=":orderId" element={<OrderDetailsPage />} />
              </Route>
            </Route>
            <Route path="/support" element={<SupportPage />} />
            <Route path="*" element={<NotFoundPage />} />
            <Route path="/contacts" element={<ContactsPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
          </Route>
          <Route path="/admin" element={<Admin />} />
        </Routes>
        <GlobalStyle />
        <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
        />
      </>
  )
}

export default App
